import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import PropTypes from 'prop-types';
import {switchSession} from '../../agentBridge/core/sessionLifecycle';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MenuIcon from '@mui/icons-material/Menu';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CropDinIcon from '@mui/icons-material/CropDin';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '../tooltip/Tooltip';
import { useHfclChat } from './useHfclChat';
import { useSpeechRecognition, detectSpeechLang } from './useSpeechRecognition';
import {
	HFCL_SPEECH_FALLBACK_LANG,
	HFCL_SPEECH_AUTO_SEND,
} from './hfclChatConfig';
import './aiChatModule.scss';
import { getHfclChatContext } from './hfclChatContext';
import { STARTER_CHIPS } from './hfclStarterChips';
import { IO_ICONS, IoIcon } from './ioIcons';
import OfferChips from './OfferChips';
import {
	fetchConversations,
	fetchConversation,
	renameConversation as renameConversationApi,
	deleteConversation as deleteConversationApi,
} from '../../agentBridge';

const STATIC_PLACEHOLDER = 'Ask or Select a topic to start';

const PANEL_WIDTH_DESKTOP = 380;
const PANEL_HEIGHT_MINIMIZED = 56;
const FLOATING_HEIGHT_RATIO = 0.5;
const VIEWPORT_MARGIN = 16;
const NAVBAR_HEIGHT = 56;
const PRIMARY_SIDEBAR_WIDTH = 58;

/**
 * Left edge of the floating panel: overlays the right half of the 58px
 * icon sidebar (same center line as the FAB), then the site tree.
 *
 * @return {number}
 */
const getFloatingDockLeft = () => Math.round(PRIMARY_SIDEBAR_WIDTH / 2) - 1;

/**
 * Bottom edge of the floating panel: viewport bottom minus a small inset.
 * The panel covers the FAB (panel z-index sits above the trigger).
 *
 * @param {number} viewportHeight
 * @param {number} [offsetTop]
 * @return {number}
 */
const getFloatingDockBottom = (viewportHeight, offsetTop = 0) =>
	viewportHeight + offsetTop - VIEWPORT_MARGIN;

/** Legacy alias used by drag constraints / defaults. */
const PANEL_WIDTH = PANEL_WIDTH_DESKTOP;

const LAYOUT = {
	FLOATING: 'floating',
	FULLSCREEN: 'fullscreen',
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Floating chat height: always ~50% of the current viewport.
 * The only cap is keeping the panel below the Canvas navbar.
 *
 * @param {number} viewportHeight
 * @return {number}
 */
const getFloatingPreferredHeight = (viewportHeight) => {
	const vh = Math.max(
		1,
		Number(viewportHeight) ||
			(typeof window !== 'undefined' ? window.innerHeight : 0) ||
			1
	);
	const target = Math.round(vh * FLOATING_HEIGHT_RATIO);
	const maxFit = Math.max(1, vh - NAVBAR_HEIGHT - VIEWPORT_MARGIN);
	return Math.min(target, maxFit);
};

/**
 * Live viewport size. Prefer the largest layout/visual measurement so
 * visualViewport=0 (first paint) or a short visual viewport never
 * under-reports height and shrink the floating chat.
 *
 * @return {{width: number, height: number, offsetTop: number}}
 */
const getViewportMetrics = () => {
	if (typeof window === 'undefined') {
		return { width: 1280, height: 800, offsetTop: 0 };
	}
	const vv = window.visualViewport;
	const innerW = Number(window.innerWidth) || 0;
	const innerH = Number(window.innerHeight) || 0;
	const clientH = Number(document.documentElement?.clientHeight) || 0;
	const clientW = Number(document.documentElement?.clientWidth) || 0;
	const vvW = Number(vv?.width) || 0;
	const vvH = Number(vv?.height) || 0;
	return {
		width: Math.round(Math.max(innerW, clientW, vvW, 1)),
		height: Math.round(Math.max(innerH, clientH, vvH, 1)),
		offsetTop: Math.round(vv?.offsetTop ?? 0),
	};
};

/**
 * Preferred floating-panel size by viewport (device) breakpoint.
 *
 * @return {{preferredWidth: number, preferredHeight: number, margin: number, breakpoint: string}}
 */
const getDevicePanelPrefs = () => {
	const { width: vw, height: vh } = getViewportMetrics();
	const preferredHeight = getFloatingPreferredHeight(vh);

	if (vw <= 480) {
		return {
			preferredWidth: Math.max(260, vw - 16),
			preferredHeight,
			margin: 8,
			breakpoint: 'sm',
		};
	}
	if (vw <= 768) {
		return {
			preferredWidth: Math.min(340, Math.max(300, vw - 32)),
			preferredHeight,
			margin: 12,
			breakpoint: 'md',
		};
	}
	if (vw <= 1024) {
		return {
			preferredWidth: 360,
			preferredHeight,
			margin: 16,
			breakpoint: 'lg',
		};
	}
	if (vw <= 1440) {
		return {
			preferredWidth: PANEL_WIDTH_DESKTOP,
			preferredHeight,
			margin: VIEWPORT_MARGIN,
			breakpoint: 'xl',
		};
	}
	return {
		preferredWidth: PANEL_WIDTH_DESKTOP,
		preferredHeight,
		margin: VIEWPORT_MARGIN,
		breakpoint: '2xl',
	};
};

/**
 * Resolve the Canvas content column beside View Site Hierarchy.
 * Prefer TopologySidebarWrapper's content shell so pages without
 * `.container-wrapper` (e.g. System Settings) still exclude the hierarchy.
 *
 * @return {Element|null}
 */
const getContainerWrapperEl = () => {
	if (typeof document === 'undefined') return null;
	return (
		document.querySelector('[data-hfcl-chat-content-shell]') ||
		document.querySelector('.container-wrapper') ||
		document.querySelector('.app-components')
	);
};

/**
 * Left inset taken by an open View Site Hierarchy sidebar (px).
 * Uses live DOM, with Redux width as fallback while mount/settle catches up.
 *
 * @param {Object} [topologyHint] - Optional `{ open, width }` from Redux.
 * @return {number}
 */
const getTopologySidebarLeftInset = (topologyHint) => {
	if (typeof document === 'undefined') return 0;
	const el = document.querySelector('[data-hfcl-topology-sidebar]');
	const rect = el?.getBoundingClientRect();
	if (rect && rect.width > 0) {
		return Math.max(0, Math.round(rect.right) - PRIMARY_SIDEBAR_WIDTH);
	}
	if (topologyHint?.open && topologyHint?.width) {
		return Math.max(0, Math.round(Number(topologyHint.width) || 0));
	}
	return 0;
};

const HFCL_BOUNDS_FLOOR_SELECTOR = '[data-hfcl-chat-bounds-floor]';

/**
 * True when a measured content shell is usable for layout.
 * During route transitions the shell is often briefly 0-width / off-screen;
 * trusting those rects docks the floating panel to the left and shrinks it.
 *
 * @param {DOMRect|ClientRect|null|undefined} rect
 * @param {number} minWidth
 * @return {boolean}
 */
const isUsableContentRect = (rect, minWidth = 320) => {
	if (!rect) return false;
	const width = Number(rect.width) || 0;
	const height = Number(rect.height) || 0;
	const left = Number(rect.left) || 0;
	const right = Number(rect.right) || 0;
	if (width < minWidth || height < 80) return false;
	if (right <= left) return false;
	// Shell should sit at/after the primary nav, not off to the far left.
	if (left < PRIMARY_SIDEBAR_WIDTH - 8 && left > -1) {
		// left can be 0 when measuring a full-bleed shell — still ok if width is full viewport.
		if (width < minWidth + PRIMARY_SIDEBAR_WIDTH) return false;
	}
	return true;
};

/**
 * Shared layout metrics for floating + fullscreen chat inside the content column.
 * Width/height adapt to device, hierarchy toggle, and optional `extras.chatBounds`.
 *
 * @param {Object} [topologyHint] - Optional `{ open, width }` from Redux.
 * @return {{left: number, top: number, width: number, height: number, topFloor: number, bottom: number, panelWidth: number, breakpoint: string, margin: number, reliable: boolean, floatingLeft: number, floatingRight: number}}
 */
const getChatLayoutMetrics = (topologyHint) => {
	const prefs = getDevicePanelPrefs();
	const viewport = getViewportMetrics();
	const topologyInset = getTopologySidebarLeftInset(topologyHint);
	const baseLeft = PRIMARY_SIDEBAR_WIDTH + topologyInset;
	const minPanelWidth = prefs.breakpoint === 'sm' ? 240 : 260;
	const floatingLeft = getFloatingDockLeft();
	const fallback = {
		left: baseLeft,
		top: Math.max(NAVBAR_HEIGHT, viewport.height + viewport.offsetTop - prefs.preferredHeight - prefs.margin),
		width: Math.max(minPanelWidth, viewport.width - baseLeft - prefs.margin),
		height: prefs.preferredHeight,
		floatingHeight: prefs.preferredHeight,
		filledHeight: prefs.preferredHeight,
		panelWidth: Math.min(
			prefs.preferredWidth,
			Math.max(minPanelWidth, viewport.width - baseLeft - prefs.margin * 2)
		),
		topFloor: NAVBAR_HEIGHT,
		bottom: viewport.height + viewport.offsetTop - prefs.margin,
		floatingBottom: getFloatingDockBottom(viewport.height, viewport.offsetTop),
		floatingLeft,
		floatingRight: viewport.width,
		breakpoint: prefs.breakpoint,
		margin: prefs.margin,
		reliable: true,
		fillAvailable: false,
	};

	if (typeof document === 'undefined') return fallback;

	const content = getContainerWrapperEl();
	const app = document.querySelector('.app-components');
	const contentRect = content?.getBoundingClientRect();
	const appRect = app?.getBoundingClientRect();

	// Prefer a stable content shell; if both are mid-transition, use viewport fallback
	// so the floating panel keeps a stable width instead of shrinking.
	const measuredRect = isUsableContentRect(contentRect, minPanelWidth)
		? contentRect
		: isUsableContentRect(appRect, minPanelWidth)
			? appRect
			: null;

	if (!measuredRect) {
		return fallback;
	}

	// Never sit under the hierarchy: content-shell left, or primary + sidebar inset.
	const left = Math.max(
		baseLeft,
		PRIMARY_SIDEBAR_WIDTH,
		Math.round(measuredRect.left ?? baseLeft)
	);
	const right = Math.round(measuredRect.right ?? viewport.width);
	const availableWidth = Math.max(minPanelWidth, right - left);

	// If available width collapsed (sidebar animation / unmount), keep viewport fallback
	// so floating dock keeps a stable preferred width.
	if (availableWidth < prefs.preferredWidth) {
		return fallback;
	}

	let topFloor = Math.max(
		NAVBAR_HEIGHT,
		Math.round(measuredRect.top ?? NAVBAR_HEIGHT)
	);
	const scope = content || app || document.body;
	let topGap = 0;
	const hasExplicitFloor = Boolean(
		scope.querySelector(HFCL_BOUNDS_FLOOR_SELECTOR)
	);

	const pushTopBelow = (el) => {
		if (!el?.getBoundingClientRect) return;
		const rect = el.getBoundingClientRect();
		if (rect.height <= 0) return;
		if (rect.top >= window.innerHeight * 0.55) return;
		if (rect.bottom > topFloor) topFloor = Math.round(rect.bottom);
	};

	scope
		.querySelectorAll('.headerAccessPoint, .headerAccessPointDashboard')
		.forEach((el) => pushTopBelow(el));

	// When a page marks an explicit floor (e.g. tab row), ignore other sticky
	// content so tab switches do not change chat height/position.
	if (!hasExplicitFloor) {
		scope.querySelectorAll('[style*="sticky"], [style*="Sticky"]').forEach((el) => {
			if (el.matches?.('[data-hfcl-chat-panel]')) return;
			if (el.closest?.(HFCL_BOUNDS_FLOOR_SELECTOR)) return;
			pushTopBelow(el);
		});
	}

	// Page floors (tab row / dashboard summary) define the chat top edge.
	scope.querySelectorAll(HFCL_BOUNDS_FLOOR_SELECTOR).forEach((el) => {
		if (!el?.getBoundingClientRect) return;
		const rect = el.getBoundingClientRect();
		const floorEdge = rect.height > 0 ? rect.bottom : rect.top;
		if (floorEdge > 0 && floorEdge > topFloor) {
			topFloor = Math.round(floorEdge);
		}
		const dataGap = Number(el.getAttribute('data-hfcl-chat-top-gap'));
		if (!Number.isNaN(dataGap) && el.hasAttribute('data-hfcl-chat-top-gap')) {
			topGap = Math.max(topGap, dataGap);
		}
	});

	const ctxBounds = getHfclChatContext()?.extras?.chatBounds || {};
	if (ctxBounds.topGap != null && ctxBounds.topGap !== '') {
		topGap = Number(ctxBounds.topGap) || 0;
	}
	topFloor += topGap;

	let bottomMargin = prefs.margin;
	scope.querySelectorAll(HFCL_BOUNDS_FLOOR_SELECTOR).forEach((el) => {
		if (!el.hasAttribute('data-hfcl-chat-bottom-gap')) return;
		const dataBottomGap = Number(el.getAttribute('data-hfcl-chat-bottom-gap'));
		if (!Number.isNaN(dataBottomGap)) {
			bottomMargin = Math.max(0, prefs.margin + dataBottomGap);
		}
	});
	if (ctxBounds.bottomMargin != null && ctxBounds.bottomMargin !== '') {
		bottomMargin = Math.max(0, Number(ctxBounds.bottomMargin) || 0);
	} else if (ctxBounds.bottomGap != null && ctxBounds.bottomGap !== '') {
		bottomMargin = Math.max(0, prefs.margin + (Number(ctxBounds.bottomGap) || 0));
	}

	// Fullscreen may use bottomMargin: 0 to fill the column. Floating docks
	// to the bottom-left at ~50% of the viewport on every route.
	const floatingBottom = getFloatingDockBottom(
		viewport.height,
		viewport.offsetTop
	);
	const bottom = viewport.height + viewport.offsetTop - bottomMargin;
	const availableHeight = Math.max(0, bottom - topFloor);
	const minHeight = Number(ctxBounds.minHeight) || (prefs.breakpoint === 'sm' ? 280 : 240);
	const heightOffset = Number(ctxBounds.heightOffset) || 0;

	const fillAvailable = Boolean(ctxBounds.fillAvailable);

	const floatingHeight = Math.min(
		prefs.preferredHeight,
		Math.max(1, floatingBottom - NAVBAR_HEIGHT)
	);
	const filledHeight = Math.max(minHeight, availableHeight + heightOffset);
	const resolvedHeight = floatingHeight;
	const top = Math.max(NAVBAR_HEIGHT, floatingBottom - floatingHeight);

	let preferredWidth = prefs.preferredWidth;
	if (ctxBounds.preferredWidth) {
		preferredWidth = Number(ctxBounds.preferredWidth) || preferredWidth;
	}
	if (ctxBounds.maxWidth) {
		preferredWidth = Math.min(preferredWidth, Number(ctxBounds.maxWidth) || preferredWidth);
	}
	const panelWidth = Math.max(
		minPanelWidth,
		Math.min(preferredWidth, availableWidth - prefs.margin * 2)
	);

	return {
		left,
		top,
		width: availableWidth,
		height: resolvedHeight,
		floatingHeight,
		filledHeight,
		panelWidth,
		topFloor,
		bottom,
		floatingBottom,
		floatingLeft,
		floatingRight: right,
		breakpoint: prefs.breakpoint,
		margin: prefs.margin,
		reliable: true,
		fillAvailable,
	};
};

/**
 * Fullscreen chat as a floating window (Zeplin): overlays Canvas, peeks the
 * icon sidebar + a sliver of the site tree, and keeps a gap below the navbar
 * and around the other edges. It does not fill the content column flush.
 *
 * @return {{left: number, top: number, width: number, height: number}}
 */
const getContextPanelBounds = () => {
	const viewport = getViewportMetrics();
	const prefs = getDevicePanelPrefs();
	const gap = prefs.margin;
	const left = PRIMARY_SIDEBAR_WIDTH + gap;
	const top = NAVBAR_HEIGHT + gap;
	const minWidth = prefs.breakpoint === 'sm' ? 240 : 280;
	const minHeight = prefs.breakpoint === 'sm' ? 280 : 240;
	const width = Math.max(minWidth, viewport.width - left - gap);
	const height = Math.max(
		minHeight,
		viewport.height + viewport.offsetTop - top - gap
	);

	return { left, top, width, height };
};

/**
 * Dock the floating panel to the bottom-left, overlaying half of the
 * icon sidebar (aligned with the FAB), then the site tree.
 * Height is ~50% of the viewport on every screen and location.
 *
 * @param {Object} [topologyHint] - Optional `{ open, width }` from Redux.
 * @return {{x: number, y: number, height: number, width: number, breakpoint: string}}
 */
const getDefaultPosition = (topologyHint) => {
	const metrics = getChatLayoutMetrics(topologyHint);
	const panelWidth = metrics.panelWidth;
	const dockBottom = metrics.floatingBottom ?? metrics.bottom;
	const floatingRight = metrics.floatingRight ?? metrics.left + metrics.width;
	const dockLeft = metrics.floatingLeft ?? getFloatingDockLeft();
	const height = metrics.floatingHeight || metrics.height;
	const maxX = floatingRight - panelWidth - metrics.margin;
	const minX = Math.min(dockLeft, Math.max(metrics.margin, maxX));
	return {
		x: clamp(dockLeft, minX, Math.max(minX, maxX)),
		y: Math.max(NAVBAR_HEIGHT, dockBottom - height),
		height,
		width: panelWidth,
		breakpoint: metrics.breakpoint,
	};
};

const syncFloatingLayout = (topologyHint) => getDefaultPosition(topologyHint);

const THREADS_PREVIEW = 5;

/**
 * Date separators:
 *   Today 12:07 PM
 *   Yesterday at 11:26 PM
 *   Jul 7, 2026 at 11:26 PM
 *
 * @param {number|string|Date} ts - Message timestamp.
 * @return {string}
 */
const formatDateSeparator = (ts) => {
	const d = ts ? new Date(ts) : new Date();
	const now = new Date();
	const sameDay =
		d.getFullYear() === now.getFullYear() &&
		d.getMonth() === now.getMonth() &&
		d.getDate() === now.getDate();
	const yesterday = new Date(now);
	yesterday.setDate(now.getDate() - 1);
	const isYesterday =
		d.getFullYear() === yesterday.getFullYear() &&
		d.getMonth() === yesterday.getMonth() &&
		d.getDate() === yesterday.getDate();
	const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	if (sameDay) return `Today ${time}`;
	if (isYesterday) return `Yesterday at ${time}`;
	const datePart = d.toLocaleDateString([], {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
	return `${datePart} at ${time}`;
};

const SCROLL_NEAR_BOTTOM_PX = 72;

/**
 * Map a server conversation summary into the shape this panel's drawer
 * already renders (`id`/`title`), so the JSX needs no changes.
 *
 * @param {object} summary `{session_id, title, updated_at, last_message_preview}`
 * @return {{id: string, title: string, updatedAt: number}}
 */
const toThreadSummary = (summary) => ({
	id: summary.session_id,
	title: summary.title,
	updatedAt: Date.parse(summary.updated_at) || Date.now(),
});

/**
 * Map a server conversation's stored messages into the chat panel's
 * lightweight message shape (role/content/createdAt).
 *
 * @param {object[]} messages `{id, role, content, created_at}[]`
 * @return {object[]} panel-shaped messages
 */
const toChatMessages = (messages) => (messages || []).map((m) => ({
	id: `history-${m.id}`,
	role: m.role,
	content: m.content,
	createdAt: Date.parse(m.created_at) || Date.now(),
}));

/**
 * Render light markdown (**bold**, `code`) for chat bubbles.
 *
 * @param {string} content Raw message text
 * @return {JSX.Element[]} Line elements with inline formatting
 */
const renderMessageContent = (content) =>
	String(content || '')
		.split('\n')
		.map((line, idx) => {
			const parts = [];
			const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
			let last = 0;
			let match = re.exec(line);
			while (match) {
				if (match.index > last) parts.push(line.slice(last, match.index));
				const token = match[0];
				if (token.startsWith('**')) {
					parts.push(<strong key={`${idx}-${match.index}`}>{token.slice(2, -2)}</strong>);
				} else {
					parts.push(
						<code key={`${idx}-${match.index}`} className="ai-chat-panel__inline-code">
							{token.slice(1, -1)}
						</code>
					);
				}
				last = match.index + token.length;
				match = re.exec(line);
			}
			if (last < line.length) parts.push(line.slice(last));
			return <div key={idx}>{parts.length ? parts : '\u00a0'}</div>;
		});

/**
 * ioGenie wordmark from the design icon set.
 *
 * @return {JSX.Element}
 */
function IoGenieLogo() {
	return (
		<img
			src={IO_ICONS.logo}
			alt="ioGenie AI Assistant"
			className="ai-chat-panel__wordmark"
			draggable={false}
		/>
	);
}

/**
 * Animated waveform bars shown during voice dictation.
 *
 * @return {JSX.Element}
 */
function WaveformBars() {
	return (
		<span className="ai-chat-panel__waveform" aria-hidden="true">
			{Array.from({ length: 24 }).map((_, i) => (
				<span
					key={i}
					className="ai-chat-panel__wave-bar"
					style={{ animationDelay: `${(i % 8) * 0.08}s` }}
				/>
			))}
		</span>
	);
}

/**
 * Floating / fullscreen io Genie chat panel.
 *
 * @param {Object} props - Component props.
 * @return {JSX.Element} The io Genie chat panel.
 */
function AiChatPanel({
	onClose,
	isMinimized,
	onMinimize,
	onRestore,
	hidden = false,
	onAction,
	onNewReplyWhileHidden,
}) {
	const panelRef = useRef(null);
	const messageListRef = useRef(null);
	const inputRef = useRef(null);
	const dragRef = useRef({ active: false, offsetX: 0, offsetY: 0 });
	const isUserPlacedRef = useRef(false);
	const [isUserPlaced, setIsUserPlaced] = useState(false);
	const stickToBottomRef = useRef(true);
	const [showJumpToLatest, setShowJumpToLatest] = useState(false);
	const [layoutBreakpoint, setLayoutBreakpoint] = useState(
		() => getDevicePanelPrefs().breakpoint
	);
	const [position, setPosition] = useState(() => {
		const layout = getDefaultPosition();
		return {
			x: layout.x,
			y: layout.y,
			height: layout.height,
			width: layout.width,
			breakpoint: layout.breakpoint,
		};
	});
	const [message, setMessage] = useState('');
	const [layoutMode, setLayoutMode] = useState(LAYOUT.FLOATING);
	const [fullscreenBounds, setFullscreenBounds] = useState(getContextPanelBounds);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [threads, setThreads] = useState([]);
	const [showAllThreads, setShowAllThreads] = useState(false);
	const [threadMenuId, setThreadMenuId] = useState(null);
	const [editingThreadId, setEditingThreadId] = useState(null);
	const [editingThreadTitle, setEditingThreadTitle] = useState('');
	const [deleteConfirmThread, setDeleteConfirmThread] = useState(null);
	const [threadSearch, setThreadSearch] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const [copyToast, setCopyToast] = useState(null);
	const [feedback, setFeedback] = useState({});
	const [isPanelActive, setIsPanelActive] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	// Match Dashboard.jsx container-wrapper sidebar-open|sidebar-closed.
	const { sideBarTopologyOpen, sidebarWidth } = useSelector(
		(state) => state.topology || {}
	);
	const topologyHint = useMemo(
		() => ({
			open: Boolean(sideBarTopologyOpen),
			width: Number(sidebarWidth) || 0,
		}),
		[sideBarTopologyOpen, sidebarWidth]
	);
	const {
		messages,
		loading,
		statusLine,
		sendMessage,
		navigateFromOffer,
		resetChat,
		setChatMessages,
		sessionIdRef,
	} = useHfclChat();

	const [handledIds, setHandledIds] = useState(() => new Set());

	const markHandled = useCallback((id) => {
		if (!id) return;
		setHandledIds((prev) => {
			if (prev.has(id)) return prev;
			const next = new Set(prev);
			next.add(id);
			return next;
		});
	}, []);

	const expireAllOffers = useCallback(() => {
		setHandledIds((prev) => {
			const next = new Set(prev);
			for (const m of messages) {
				if (m.role === 'assistant') next.add(m.id);
			}
			return next;
		});
	}, [messages]);

	const detectedSpeechLang = useState(() =>
		detectSpeechLang(HFCL_SPEECH_FALLBACK_LANG)
	)[0];

	const speechCancelRef = useRef(false);
	const pendingTranscriptRef = useRef('');
	const transcribeTimerRef = useRef(null);
	/** idle | recording | transcribing — Zeplin voice input states */
	const [voicePhase, setVoicePhase] = useState('idle');

	const clearTranscribeTimer = useCallback(() => {
		if (transcribeTimerRef.current) {
			window.clearTimeout(transcribeTimerRef.current);
			transcribeTimerRef.current = null;
		}
	}, []);

	const finishTranscription = useCallback(
		(rawText) => {
			clearTranscribeTimer();
			if (speechCancelRef.current) {
				speechCancelRef.current = false;
				pendingTranscriptRef.current = '';
				setVoicePhase('idle');
				return;
			}
			const trimmed = (rawText || pendingTranscriptRef.current || '').trim();
			pendingTranscriptRef.current = '';
			if (!trimmed) {
				setVoicePhase('idle');
				return;
			}
			if (HFCL_SPEECH_AUTO_SEND) {
				setMessage('');
				setVoicePhase('idle');
				setHandledIds((prev) => {
					const next = new Set(prev);
					for (const mm of messages) {
						if (mm.role === 'assistant') next.add(mm.id);
					}
					return next;
				});
				sendMessage(trimmed, navigate, {
					fromVoice: true,
					voiceLang: detectedSpeechLang,
				});
				return;
			}
			// Zeplin: place transcript in the composer and enable Send.
			setMessage(trimmed);
			setVoicePhase('idle');
			requestAnimationFrame(() => {
				inputRef.current?.focus?.();
			});
		},
		[
			clearTranscribeTimer,
			messages,
			navigate,
			sendMessage,
			detectedSpeechLang,
		]
	);

	const handleSpeechFinal = useCallback(
		(text) => {
			if (speechCancelRef.current) {
				return;
			}
			const trimmed = (text || '').trim();
			if (!trimmed) return;
			pendingTranscriptRef.current = trimmed;
		},
		[]
	);

	const speech = useSpeechRecognition({
		lang: detectedSpeechLang,
		onFinal: handleSpeechFinal,
	});

	// Keep the latest interim/final transcript while dictating.
	useEffect(() => {
		if (voicePhase === 'recording' && speech.transcript) {
			pendingTranscriptRef.current = speech.transcript;
		}
	}, [voicePhase, speech.transcript]);

	// If the browser ends recognition without Stop, still run Transcribing → input.
	const hadListeningRef = useRef(false);
	useEffect(() => {
		if (speech.listening) {
			hadListeningRef.current = true;
			return;
		}
		if (
			voicePhase === 'recording' &&
			hadListeningRef.current &&
			!speechCancelRef.current
		) {
			hadListeningRef.current = false;
			setVoicePhase('transcribing');
		}
		if (voicePhase === 'idle') {
			hadListeningRef.current = false;
		}
	}, [voicePhase, speech.listening]);

	// After Stop: show Transcribing… briefly, then fill the input.
	useEffect(() => {
		if (voicePhase !== 'transcribing') return undefined;
		if (speech.listening) return undefined;

		clearTranscribeTimer();
		transcribeTimerRef.current = window.setTimeout(() => {
			finishTranscription(pendingTranscriptRef.current);
		}, 700);

		return () => clearTranscribeTimer();
	}, [voicePhase, speech.listening, clearTranscribeTimer, finishTranscription]);

	const handleMicClick = () => {
		if (!speech.supported || voicePhase !== 'idle') return;
		speechCancelRef.current = false;
		pendingTranscriptRef.current = '';
		hadListeningRef.current = false;
		setMessage('');
		setVoicePhase('recording');
		speech.start();
	};

	const handleStopDictating = () => {
		if (voicePhase !== 'recording') return;
		setVoicePhase('transcribing');
		speech.stop();
	};

	const handleCancelRecording = () => {
		speechCancelRef.current = true;
		pendingTranscriptRef.current = '';
		hadListeningRef.current = false;
		clearTranscribeTimer();
		setVoicePhase('idle');
		setMessage('');
		if (speech.listening) speech.stop();
	};

	const [inputFocused, setInputFocused] = useState(false);
	const inputPlaceholder = STATIC_PLACEHOLDER;

	const hasContent = Boolean(message.trim());
	const isVoiceBusy = voicePhase === 'recording' || voicePhase === 'transcribing';

	const latestUserMessageId = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i -= 1) {
			if (messages[i].role === 'user') return messages[i].id;
		}
		return null;
	}, [messages]);

	const visibleThreads = useMemo(() => {
		let list = threads;
		if (threadSearch.trim()) {
			const q = threadSearch.trim().toLowerCase();
			list = list.filter((t) => String(t.title || '').toLowerCase().includes(q));
		}
		return showAllThreads ? list : list.slice(0, THREADS_PREVIEW);
	}, [threads, showAllThreads, threadSearch]);

	const constrainPosition = useCallback((x, y) => {
		const panelEl = panelRef.current;
		const viewport = getViewportMetrics();
		const width = panelEl?.offsetWidth ?? position.width ?? PANEL_WIDTH;
		const height =
			panelEl?.offsetHeight ??
			(isMinimized
				? PANEL_HEIGHT_MINIMIZED
				: position.height ?? getFloatingPreferredHeight(viewport.height));

		const minX = 0;
		const maxX = viewport.width - width;
		const minY = NAVBAR_HEIGHT;
		const maxY = viewport.height + viewport.offsetTop - height;

		return {
			x: clamp(x, minX, Math.max(minX, maxX)),
			y: clamp(y, minY, Math.max(minY, maxY)),
		};
	}, [isMinimized, position.height, position.width]);

	const applyFloatingLayout = useCallback((layout, options = {}) => {
		const preserveSize = Boolean(options.preserveSize);
		setPosition((prev) => {
			const metrics = getChatLayoutMetrics(topologyHint);
			const dockBottom = metrics.floatingBottom ?? metrics.bottom;
			const maxFitHeight = Math.max(1, dockBottom - NAVBAR_HEIGHT);
			const width = preserveSize && prev.width ? prev.width : layout.width;
			const height = Math.min(layout.height, maxFitHeight);

			// After a user drag, keep their spot and only clamp to the
			// viewport below the Canvas navbar.
			if (isUserPlacedRef.current) {
				const viewport = getViewportMetrics();
				const panelWidth = panelRef.current?.offsetWidth || width;
				const panelHeight = panelRef.current?.offsetHeight || height;
				const minX = 0;
				const maxX = viewport.width - panelWidth;
				const minY = NAVBAR_HEIGHT;
				const maxY = viewport.height + viewport.offsetTop - panelHeight;
				return {
					x: clamp(prev.x, minX, Math.max(minX, maxX)),
					y: clamp(prev.y, minY, Math.max(minY, maxY)),
					width,
					height,
					breakpoint: layout.breakpoint || prev.breakpoint,
				};
			}

			if (preserveSize && prev.width && prev.height) {
				const floatingRight = metrics.floatingRight ?? metrics.left + metrics.width;
				const dockLeft = metrics.floatingLeft ?? getFloatingDockLeft();
				const maxX = floatingRight - width - metrics.margin;
				const minX = Math.min(dockLeft, Math.max(metrics.margin, maxX));
				const minY = NAVBAR_HEIGHT;
				const maxY = dockBottom - height;
				let nextY = clamp(prev.y, minY, Math.max(minY, maxY));
				if (nextY + height > dockBottom) {
					nextY = Math.max(minY, dockBottom - height);
				}
				return {
					x: clamp(prev.x, minX, Math.max(minX, maxX)),
					y: nextY,
					width,
					height,
					breakpoint: layout.breakpoint || prev.breakpoint,
				};
			}
			return {
				x: layout.x,
				y: layout.y,
				height,
				width,
				breakpoint: layout.breakpoint,
			};
		});
	}, [topologyHint]);

	useEffect(() => {
		const handleResize = () => {
			setLayoutBreakpoint(getDevicePanelPrefs().breakpoint);
			if (layoutMode === LAYOUT.FLOATING) {
				applyFloatingLayout(syncFloatingLayout(topologyHint));
			}
			if (layoutMode === LAYOUT.FULLSCREEN) {
				setFullscreenBounds(getContextPanelBounds());
			}
		};
		window.addEventListener('resize', handleResize);
		window.addEventListener('orientationchange', handleResize);
		const vv = window.visualViewport;
		vv?.addEventListener('resize', handleResize);
		vv?.addEventListener('scroll', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('orientationchange', handleResize);
			vv?.removeEventListener('resize', handleResize);
			vv?.removeEventListener('scroll', handleResize);
		};
	}, [layoutMode, applyFloatingLayout, topologyHint]);

	// Keep chat aligned to the content column when View Site Hierarchy
	// toggles / resizes, or when sticky page floors change size.
	useEffect(() => {
		if (hidden) return undefined;

		const refreshLayout = ({ preserveFloatingSize = false } = {}) => {
			setLayoutBreakpoint(getDevicePanelPrefs().breakpoint);
			if (layoutMode === LAYOUT.FULLSCREEN) {
				setFullscreenBounds(getContextPanelBounds());
			} else if (layoutMode === LAYOUT.FLOATING) {
				applyFloatingLayout(syncFloatingLayout(topologyHint), {
					preserveSize: preserveFloatingSize,
				});
			}
		};

		const scheduleRefresh = (opts) => {
			refreshLayout(opts);
			if (typeof window.requestAnimationFrame === 'function') {
				window.requestAnimationFrame(() => {
					refreshLayout(opts);
					window.requestAnimationFrame(() => refreshLayout(opts));
				});
			}
		};

		// Initial dock uses ~50% viewport height on every page.
		scheduleRefresh({ preserveFloatingSize: false });
		// Hierarchy open/close can settle over a short CSS/layout window.
		const settleTimers = [50, 180, 320].map((ms) =>
			window.setTimeout(
				() => scheduleRefresh({ preserveFloatingSize: false }),
				ms
			)
		);

		const onTopologySidebarChange = () =>
			scheduleRefresh({ preserveFloatingSize: false });
		window.addEventListener('hfcl-topology-sidebar-change', onTopologySidebarChange);

		// Forms/modals remount page content and fire resize/scroll — keep the
		// floating panel's existing height so opening Add SSID etc. does not
		// reshape the chat.
		const onContentDrivenRefresh = () =>
			scheduleRefresh({ preserveFloatingSize: true });

		const scrollRoot =
			document.querySelector('.DashboardWrapper') ||
			document.querySelector('.app-components') ||
			window;
		scrollRoot.addEventListener('scroll', onContentDrivenRefresh, { passive: true });
		window.addEventListener('scroll', onContentDrivenRefresh, { passive: true });

		const observers = [];
		if (typeof ResizeObserver !== 'undefined') {
			const ro = new ResizeObserver(onContentDrivenRefresh);
			const shell = getContainerWrapperEl();
			const app = document.querySelector('.app-components');
			const topologySidebar = document.querySelector('[data-hfcl-topology-sidebar]');
			const scope = shell || app || document.body;
			if (shell) ro.observe(shell);
			if (app && app !== shell) ro.observe(app);
			if (topologySidebar) ro.observe(topologySidebar);
			scope.querySelectorAll(HFCL_BOUNDS_FLOOR_SELECTOR).forEach((el) => {
				ro.observe(el);
			});
			observers.push(ro);
		}

		return () => {
			settleTimers.forEach((id) => window.clearTimeout(id));
			window.removeEventListener('hfcl-topology-sidebar-change', onTopologySidebarChange);
			scrollRoot.removeEventListener('scroll', onContentDrivenRefresh);
			window.removeEventListener('scroll', onContentDrivenRefresh);
			observers.forEach((o) => o.disconnect?.());
		};
	}, [layoutMode, hidden, topologyHint, applyFloatingLayout]);

	// After route changes the content shell remounts with transient 0-width
	// rects. Re-dock floating/fullscreen once the new page has settled so
	// the panel does not stay stuck with a shrunk width.
	useEffect(() => {
		if (hidden) return undefined;

		const refreshAfterNav = () => {
			setLayoutBreakpoint(getDevicePanelPrefs().breakpoint);
			if (layoutMode === LAYOUT.FULLSCREEN) {
				setFullscreenBounds(getContextPanelBounds());
			} else if (layoutMode === LAYOUT.FLOATING) {
				applyFloatingLayout(syncFloatingLayout(topologyHint));
			}
		};

		const timers = [0, 80, 200, 450].map((ms) =>
			window.setTimeout(refreshAfterNav, ms)
		);
		return () => timers.forEach((id) => window.clearTimeout(id));
	}, [
		location.pathname,
		hidden,
		layoutMode,
		topologyHint,
		applyFloatingLayout,
	]);

	useEffect(() => {
		const handleMouseMove = (e) => {
			if (!dragRef.current.active) return;
			const next = constrainPosition(
				e.clientX - dragRef.current.offsetX,
				e.clientY - dragRef.current.offsetY
			);
			setPosition((prev) => ({ ...prev, x: next.x, y: next.y }));
		};
		const handleMouseUp = () => {
			dragRef.current.active = false;
		};
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [constrainPosition]);

	useLayoutEffect(() => {
		if (
			!isUserPlaced ||
			hidden ||
			isMinimized ||
			layoutMode !== LAYOUT.FLOATING
		) {
			return;
		}
		setPosition((prev) => {
			const next = constrainPosition(prev.x, prev.y);
			if (next.x === prev.x && next.y === prev.y) return prev;
			return { ...prev, ...next };
		});
	}, [isUserPlaced, hidden, isMinimized, layoutMode, constrainPosition, messages.length]);

	const isMessagesNearBottom = useCallback((el) => {
		if (!el) return true;
		return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_NEAR_BOTTOM_PX;
	}, []);

	const updateJumpToLatestVisibility = useCallback(() => {
		const list = messageListRef.current;
		if (!list) {
			setShowJumpToLatest(false);
			return;
		}
		const near = isMessagesNearBottom(list);
		stickToBottomRef.current = near;
		const canScroll = list.scrollHeight > list.clientHeight + 4;
		setShowJumpToLatest(canScroll && !near);
	}, [isMessagesNearBottom]);

	const handleMessagesScroll = useCallback(() => {
		updateJumpToLatestVisibility();
	}, [updateJumpToLatestVisibility]);

	const jumpToLatest = useCallback(() => {
		const list = messageListRef.current;
		if (!list) return;
		stickToBottomRef.current = true;
		setShowJumpToLatest(false);
		list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
	}, []);

	const scrollMessagesToBottom = useCallback((behavior = 'auto') => {
		const list = messageListRef.current;
		if (!list) return;
		if (behavior === 'smooth') {
			list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
		} else {
			list.scrollTop = list.scrollHeight;
		}
	}, []);

	useEffect(() => {
		if (!stickToBottomRef.current) {
			updateJumpToLatestVisibility();
			return;
		}
		scrollMessagesToBottom('auto');
		updateJumpToLatestVisibility();
	}, [
		messages,
		loading,
		statusLine,
		scrollMessagesToBottom,
		updateJumpToLatestVisibility,
	]);

	useEffect(() => {
		if (hidden) return;
		const raf = requestAnimationFrame(() => {
			stickToBottomRef.current = true;
			scrollMessagesToBottom('auto');
			updateJumpToLatestVisibility();
			if (inputRef.current && typeof inputRef.current.focus === 'function') {
				inputRef.current.focus();
			}
		});
		return () => cancelAnimationFrame(raf);
	}, [hidden, scrollMessagesToBottom, updateJumpToLatestVisibility]);

	// Re-focus input after the assistant finishes a reply (Zeplin).
	const prevLoadingRef = useRef(loading);
	useEffect(() => {
		const wasLoading = prevLoadingRef.current;
		prevLoadingRef.current = loading;
		if (wasLoading && !loading && !hidden && !speech.listening) {
			requestAnimationFrame(() => {
				inputRef.current?.focus?.();
			});
		}
	}, [loading, hidden, speech.listening]);

	useEffect(() => {
		const el = inputRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
	}, [message]);

	const prevMsgCountRef = useRef(messages.length);
	useEffect(() => {
		const prevCount = prevMsgCountRef.current;
		prevMsgCountRef.current = messages.length;
		if (!hidden) return;
		if (messages.length <= prevCount) return;
		const last = messages[messages.length - 1];
		if (last?.role !== 'assistant') return;
		if (typeof onNewReplyWhileHidden === 'function') {
			onNewReplyWhileHidden();
		}
	}, [messages, hidden, onNewReplyWhileHidden]);

	useEffect(() => {
		if (!copyToast) return undefined;
		const t = setTimeout(() => setCopyToast(null), 1800);
		return () => clearTimeout(t);
	}, [copyToast]);

	useEffect(() => {
		if (!threadMenuId) return undefined;
		const onDocClick = (e) => {
			if (!e.target.closest?.('.ai-chat-panel__thread-menu') &&
				!e.target.closest?.('.ai-chat-panel__thread-more')) {
				setThreadMenuId(null);
			}
		};
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [threadMenuId]);

	const dockToDefaultFloating = useCallback(() => {
		isUserPlacedRef.current = false;
		setIsUserPlaced(false);
		setLayoutMode(LAYOUT.FLOATING);
		applyFloatingLayout(syncFloatingLayout(topologyHint));
	}, [applyFloatingLayout, topologyHint]);

	const collapseToActiveBar = useCallback(() => {
		if (isMinimized || hidden) return;
		setDrawerOpen(false);
		if (layoutMode === LAYOUT.FULLSCREEN) {
			dockToDefaultFloating();
		}
		if (typeof onMinimize === 'function') onMinimize();
	}, [isMinimized, hidden, layoutMode, dockToDefaultFloating, onMinimize]);

	const restoreFromBar = useCallback(() => {
		if (!isMinimized) return;
		if (typeof onRestore === 'function') onRestore();
	}, [isMinimized, onRestore]);

	// Zeplin: click outside the Genie window collapses it to the active bar
	// so Canvas stays usable and the conversation is not lost.
	useEffect(() => {
		if (hidden || isMinimized) return undefined;
		const onPointerDown = (event) => {
			if (event.button && event.button !== 0) return;
			const target = event.target;
			if (!(target instanceof Element)) return;
			if (target.closest('[data-hfcl-chat-panel]')) return;
			if (target.closest('[data-hfcl-chat-fab]')) return;
			if (target.closest('.MuiTooltip-popper')) return;
			collapseToActiveBar();
		};
		document.addEventListener('pointerdown', onPointerDown, true);
		return () => document.removeEventListener('pointerdown', onPointerDown, true);
	}, [hidden, isMinimized, collapseToActiveBar]);

	const focusInput = () => {
		requestAnimationFrame(() => {
			inputRef.current?.focus?.();
		});
	};

	// Chat history now lives server-side (SQLite, keyed by session id) - each
	// `/api/agent/chat` turn is persisted there as it happens, so there is no
	// client-side "archive on close" step any more. The drawer just re-fetches
	// the current list whenever it's opened.
	const refreshThreads = useCallback(() => {
		fetchConversations({limit: 50})
			.then((page) => {
				setThreads((page.conversations || []).map(toThreadSummary));
			})
			.catch(() => {
				// Best-effort - keep whatever was last shown rather than clearing it.
			});
	}, []);

	useEffect(() => {
		if (drawerOpen) refreshThreads();
	}, [drawerOpen, refreshThreads]);

	const handleDragStart = (e) => {
		if (layoutMode !== LAYOUT.FLOATING) return;
		if (isMinimized) return;
		if (e.target.closest?.('button')) return;
		e.preventDefault();
		const rect = panelRef.current?.getBoundingClientRect();
		if (!rect) return;
		isUserPlacedRef.current = true;
		setIsUserPlaced(true);
		setPosition((prev) => ({
			...prev,
			x: rect.left,
			y: rect.top,
		}));
		dragRef.current = {
			active: true,
			offsetX: e.clientX - rect.left,
			offsetY: e.clientY - rect.top,
		};
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		const trimmed = message.trim();
		// `loading` as well, which the starter chips already check and this
		// did not. A turn takes seconds - longer on a CPU-only deployment -
		// and Enter is the natural thing to press again when nothing appears
		// to be happening. Each press was a whole turn: several at once race
		// on one session, so replies arrive out of order and the last one to
		// finish overwrites the state, which need not be the last one sent.
		if (!trimmed || isVoiceBusy || loading) return;
		expireAllOffers();
		sendMessage(trimmed, navigate);
		setMessage('');
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	const handleOfferYes = (offer, messageId) => {
		markHandled(messageId);
		navigateFromOffer(offer, navigate);
		if (typeof onAction === 'function') onAction();
	};

	const handleOfferNo = (messageId) => {
		markHandled(messageId);
		sendMessage('no', navigate);
	};

	const handleSuggestionClick = (suggestion, messageId) => {
		markHandled(messageId);
		expireAllOffers();
		// Prefer Tier-0 next_intent when present. Domain-only chips (ambiguous
		// disambiguation) re-submit the label so BE can open that domain menu.
		sendMessage(
			suggestion.label,
			navigate,
			suggestion.choice_id
				? {choiceId: suggestion.choice_id}
				: suggestion.next_intent ? {intent: suggestion.next_intent} : undefined,
		);
	};

	const [selectedChip, setSelectedChip] = useState(null);

	const handleStarterChip = (label) => {
		if (loading) return;
		setSelectedChip(label);
		expireAllOffers();
		sendMessage(label, navigate);
		focusInput();
	};

	useEffect(() => {
		if (!loading) setSelectedChip(null);
	}, [loading]);

	const handleCloseAndReset = () => {
		resetChat();
		setHandledIds(new Set());
		setMessage('');
		setSelectedChip(null);
		setDrawerOpen(false);
		setLayoutMode(LAYOUT.FLOATING);
		isUserPlacedRef.current = false;
		setIsUserPlaced(false);
		if (typeof onClose === 'function') onClose();
	};

	const handleNewChat = () => {
		restoreFromBar();
		resetChat();
		setHandledIds(new Set());
		setMessage('');
		setSelectedChip(null);
		stickToBottomRef.current = true;
		setShowJumpToLatest(false);
		// Keep Recents visible in fullscreen side-by-side layout.
		if (layoutMode !== LAYOUT.FULLSCREEN) {
			setDrawerOpen(false);
		}
		requestAnimationFrame(() => {
			const list = messageListRef.current;
			if (list) list.scrollTop = 0;
			focusInput();
		});
	};

	const handleCopy = async (text, kind) => {
		try {
			await navigator.clipboard.writeText(String(text || ''));
			setCopyToast(kind === 'user' ? 'Prompt copied' : 'Response copied');
		} catch (_) {
			setCopyToast('Unable to copy');
		}
	};

	const handleFeedback = (messageId, value) => {
		setFeedback((prev) => ({
			...prev,
			[messageId]: prev[messageId] === value ? null : value,
		}));
	};

	const handleEditLatestUser = (msg) => {
		if (!msg || msg.id !== latestUserMessageId) return;
		// Zeplin: pull the latest query into the active input for edit/resubmit.
		// Do not clear or truncate chat history.
		setMessage(String(msg.content || ''));
		focusInput();
	};

	const openThread = (thread) => {
		if (!thread?.id) return;
		fetchConversation(thread.id)
			.then((conversation) => {
				setChatMessages(toChatMessages(conversation.messages));
				// Continue under the SAME session id server-side, so this is a
				// genuine resume (new turns append to this conversation's
				// history) rather than a disconnected fresh session.
				sessionIdRef.current = thread.id;
				// And tell the shared lifecycle, which is what everything
				// else asks. Changing the ref alone left the bridge still
				// holding the thread just left: commands issued for the
				// restored thread were rejected as belonging to another
				// conversation, while work from the abandoned one was still
				// treated as current - the failure exactly inverted.
				//
				// A switch rather than an adopt: the previous thread's
				// outstanding work is not wanted here, and switching cancels
				// it.
				switchSession(thread.id);
			})
			.catch(() => {
				setCopyToast('Could not open that conversation');
			});
		setHandledIds(new Set());
		setMessage('');
		// Keep Recents visible in fullscreen side-by-side layout.
		if (layoutMode !== LAYOUT.FULLSCREEN) {
			setDrawerOpen(false);
		}
		setThreadMenuId(null);
		focusInput();
	};

	const startRenameThread = (thread) => {
		setEditingThreadId(thread.id);
		setEditingThreadTitle(thread.title || '');
		setThreadMenuId(null);
	};

	const commitRenameThread = () => {
		if (!editingThreadId) return;
		const title = editingThreadTitle.trim() || 'Untitled conversation';
		const targetId = editingThreadId;
		setThreads((prev) => prev.map((t) => (t.id === targetId ? { ...t, title } : t)));
		setEditingThreadId(null);
		setEditingThreadTitle('');
		renameConversationApi(targetId, title).catch(() => {
			refreshThreads();
		});
	};

	const confirmDeleteThread = () => {
		if (!deleteConfirmThread) return;
		const targetId = deleteConfirmThread.id;
		setThreads((prev) => prev.filter((t) => t.id !== targetId));
		setDeleteConfirmThread(null);
		setThreadMenuId(null);
		// Deleting the conversation currently open in the main chat leaves it
		// showing a transcript that no longer exists server-side, and any
		// further reply would (re)create the very row just removed - so clear
		// the view and start a fresh session, exactly like New Chat.
		if (targetId === sessionIdRef.current) {
			resetChat();
			setHandledIds(new Set());
			setMessage('');
		}
		deleteConversationApi(targetId).catch(() => {
			refreshThreads();
		});
	};

	const setLayout = (mode) => {
		if (mode === LAYOUT.FULLSCREEN && layoutMode !== LAYOUT.FULLSCREEN) {
			setFullscreenBounds(getContextPanelBounds());
			setDrawerOpen(true);
		}
		if (mode !== LAYOUT.FULLSCREEN && layoutMode === LAYOUT.FULLSCREEN) {
			setDrawerOpen(false);
		}
		if (mode === LAYOUT.FLOATING) {
			dockToDefaultFloating();
			return;
		}
		setLayoutMode(mode);
	};

	const toggleFullscreen = () => {
		restoreFromBar();
		if (layoutMode === LAYOUT.FULLSCREEN) {
			setLayout(LAYOUT.FLOATING);
			return;
		}
		setLayout(LAYOUT.FULLSCREEN);
	};

	const handleHeaderClick = (event) => {
		if (!isMinimized) return;
		if (event.target.closest?.('button')) return;
		restoreFromBar();
	};

	const handleHistoryClick = () => {
		if (isMinimized) {
			restoreFromBar();
			setDrawerOpen(true);
			return;
		}
		setDrawerOpen((open) => !open);
	};

	const hasConversation = messages.some((m) => m.role === 'user');
	const isCompactWelcome =
		layoutMode === LAYOUT.FLOATING && !isMinimized && !hasConversation;

	const deviceBreakpoint = layoutBreakpoint || getDevicePanelPrefs().breakpoint;

	const panelClassName = [
		'ai-chat-panel',
		isMinimized ? 'ai-chat-panel--minimized' : '',
		`ai-chat-panel--${isMinimized ? LAYOUT.FLOATING : layoutMode}`,
		isCompactWelcome ? 'ai-chat-panel--compact' : '',
		isPanelActive || inputFocused ? 'ai-chat-panel--active' : '',
		drawerOpen ? 'ai-chat-panel--drawer-open' : '',
		`ai-chat-panel--bp-${deviceBreakpoint}`,
	]
		.filter(Boolean)
		.join(' ');

	const floatingHeightCss = '50dvh';
	const floatingMaxHeightCss = `calc(100dvh - ${NAVBAR_HEIGHT + VIEWPORT_MARGIN}px)`;
	const defaultDockLeft = getFloatingDockLeft();

	const panelStyle = isMinimized
		? {
				left: defaultDockLeft,
				top: 'auto',
				right: 'auto',
				bottom: VIEWPORT_MARGIN,
				width: 'max-content',
				height: PANEL_HEIGHT_MINIMIZED,
				maxHeight: PANEL_HEIGHT_MINIMIZED,
				display: hidden ? 'none' : undefined,
			}
		: layoutMode === LAYOUT.FULLSCREEN
			? {
					left: fullscreenBounds.left,
					top: fullscreenBounds.top,
					width: fullscreenBounds.width,
					height: fullscreenBounds.height,
					right: 'auto',
					bottom: 'auto',
					display: hidden ? 'none' : undefined,
				}
			: {
					left: position.x,
					top: isUserPlaced ? position.y : 'auto',
					right: 'auto',
					bottom: isUserPlaced ? 'auto' : VIEWPORT_MARGIN,
					width: position.width,
					height: isCompactWelcome ? 'auto' : floatingHeightCss,
					maxHeight: floatingMaxHeightCss,
					display: hidden ? 'none' : undefined,
				};

	return (
		<div
			ref={panelRef}
			className={panelClassName}
			style={panelStyle}
			role="dialog"
			aria-label={
				isMinimized
					? 'io Genie AI Assistant, minimized. Click to restore.'
					: 'io Genie AI Assistant'
			}
			data-hfcl-chat-panel="true"
			onMouseDown={(e) => e.stopPropagation()}
			onClick={handleHeaderClick}
			onMouseEnter={() => setIsPanelActive(true)}
			onMouseLeave={() => setIsPanelActive(false)}
		>
			{drawerOpen && (
				<aside className="ai-chat-panel__drawer" aria-label="Chat history">
					<div className="ai-chat-panel__drawer-header">
						<button
							type="button"
							className="ai-chat-panel__drawer-new"
							onClick={handleNewChat}
						>
							<IoIcon src={IO_ICONS.newChat} />
							<span>New Chat</span>
						</button>
						{layoutMode !== LAYOUT.FULLSCREEN && (
							<button
								type="button"
								className="ai-chat-panel__icon-btn"
								onClick={() => {
									setDrawerOpen(false);
									setShowAllThreads(false);
									setSearchOpen(false);
									setThreadSearch('');
								}}
								aria-label="Close history drawer"
							>
								<MenuIcon />
							</button>
						)}
					</div>

					<div className="ai-chat-panel__drawer-recents">
						<div className="ai-chat-panel__drawer-recents-head">
							<span>Recents</span>
							<button
								type="button"
								className="ai-chat-panel__icon-btn"
								onClick={() => setSearchOpen((v) => !v)}
								aria-label="Search threads"
							>
								<SearchOutlinedIcon fontSize="inherit" />
							</button>
						</div>
						{searchOpen && (
							<input
								type="search"
								className="ai-chat-panel__drawer-search"
								placeholder="Search conversations"
								value={threadSearch}
								onChange={(e) => setThreadSearch(e.target.value)}
								autoFocus
							/>
						)}
						<ul className="ai-chat-panel__thread-list">
							{visibleThreads.map((thread) => (
								<li
									key={thread.id}
									className={`ai-chat-panel__thread-item${
										threadMenuId === thread.id ? ' ai-chat-panel__thread-item--active' : ''
									}`}
								>
									{editingThreadId === thread.id ? (
										<input
											className="ai-chat-panel__thread-rename"
											value={editingThreadTitle}
											onChange={(e) => setEditingThreadTitle(e.target.value)}
											onBlur={commitRenameThread}
											onKeyDown={(e) => {
												if (e.key === 'Enter') commitRenameThread();
												if (e.key === 'Escape') {
													setEditingThreadId(null);
													setEditingThreadTitle('');
												}
											}}
											autoFocus
										/>
									) : (
										<button
											type="button"
											className="ai-chat-panel__thread-title"
											onClick={() => openThread(thread)}
										>
											{thread.title}
										</button>
									)}
									<button
										type="button"
										className="ai-chat-panel__thread-more"
										aria-label="Thread actions"
										onClick={(e) => {
											e.stopPropagation();
											setThreadMenuId((id) => (id === thread.id ? null : thread.id));
										}}
									>
										<IoIcon src={IO_ICONS.more} size={16} />
									</button>
									{threadMenuId === thread.id && (
										<div className="ai-chat-panel__thread-menu" role="menu">
											<button
												type="button"
												role="menuitem"
												onClick={() => startRenameThread(thread)}
											>
												<IoIcon src={IO_ICONS.edit} size={16} />
												Rename thread
											</button>
											<button
												type="button"
												role="menuitem"
												className="ai-chat-panel__thread-menu-danger"
												onClick={() => {
													setDeleteConfirmThread(thread);
													setThreadMenuId(null);
												}}
											>
												<DeleteOutlineIcon fontSize="inherit" />
												Delete thread
											</button>
										</div>
									)}
								</li>
							))}
							{!showAllThreads && threads.length > THREADS_PREVIEW && (
								<li>
									<button
										type="button"
										className="ai-chat-panel__thread-more-link"
										onClick={() => setShowAllThreads(true)}
									>
										… More
									</button>
								</li>
							)}
							{showAllThreads && threads.length > THREADS_PREVIEW && (
								<li>
									<button
										type="button"
										className="ai-chat-panel__thread-more-link"
										onClick={() => setShowAllThreads(false)}
									>
										Show less
									</button>
								</li>
							)}
							{threads.length === 0 && (
								<li className="ai-chat-panel__thread-empty">No recent conversations</li>
							)}
						</ul>
					</div>
				</aside>
			)}

			<div className="ai-chat-panel__main">
				<div
					className="ai-chat-panel__header"
					onMouseDown={handleDragStart}
				>
					<div className="ai-chat-panel__header-left">
						<button
							type="button"
							className="ai-chat-panel__icon-btn"
							onClick={handleHistoryClick}
							aria-label={drawerOpen ? 'Hide chat history' : 'Show chat history'}
							title="Chat history"
						>
							<MenuIcon />
						</button>
						<IoGenieLogo />
					</div>

					<div className="ai-chat-panel__header-actions">
						<Tooltip content="New Chat" type="basic" direction="bottom" style={{ width: '90px' }}>
							<button
								type="button"
								className="ai-chat-panel__icon-btn"
								onClick={handleNewChat}
								aria-label="Start a new chat"
							>
								<IoIcon src={IO_ICONS.newChat} />
							</button>
						</Tooltip>

						<Tooltip
							content={
								layoutMode === LAYOUT.FULLSCREEN
									? 'Exit Fullscreen'
									: 'Fullscreen'
							}
							type="basic"
							direction="bottom"
							style={{ width: '120px' }}
						>
							<button
								type="button"
								className={
									layoutMode === LAYOUT.FULLSCREEN
										? 'ai-chat-panel__icon-btn ai-chat-panel__icon-btn--active'
										: 'ai-chat-panel__icon-btn'
								}
								onClick={toggleFullscreen}
								aria-label={
									layoutMode === LAYOUT.FULLSCREEN
										? 'Exit Fullscreen'
										: 'Fullscreen'
								}
								aria-pressed={layoutMode === LAYOUT.FULLSCREEN}
							>
								{layoutMode === LAYOUT.FULLSCREEN ? (
									<CloseFullscreenIcon />
								) : (
									<OpenInFullIcon />
								)}
							</button>
						</Tooltip>

						<Tooltip
							content={isMinimized ? 'Maximize' : 'Minimize'}
							type="basic"
							direction="bottom"
							style={{ width: '90px' }}
						>
							<button
								type="button"
								className="ai-chat-panel__icon-btn"
								onClick={isMinimized ? restoreFromBar : collapseToActiveBar}
								aria-label={isMinimized ? 'Maximize chat' : 'Minimize chat'}
							>
								{isMinimized ? (
									<CropDinIcon />
								) : (
									<IoIcon src={IO_ICONS.minimise} />
								)}
							</button>
						</Tooltip>
						<button
							type="button"
							className="ai-chat-panel__icon-btn"
							onClick={handleCloseAndReset}
							aria-label="Close io Genie and clear the conversation"
							title="Close"
						>
							<IoIcon src={IO_ICONS.close} size={16} />
						</button>
					</div>
				</div>

				{!isMinimized && (
					<>
						<div className="ai-chat-panel__messages-wrap">
							<div
								className="ai-chat-panel__messages"
								ref={messageListRef}
								onScroll={handleMessagesScroll}
								aria-live="polite"
							>
							{copyToast && (
								<div className="ai-chat-panel__copy-toast" role="status">
									{copyToast}
								</div>
							)}

							{messages[0]?.createdAt && (
								<div className="ai-chat-panel__date-sep">
									{formatDateSeparator(messages[0].createdAt)}
								</div>
							)}

							{messages.map((m, index) => {
								const prev = messages[index - 1];
								const showSep =
									prev &&
									m.createdAt &&
									prev.createdAt &&
									Math.abs(m.createdAt - prev.createdAt) > 6 * 60 * 60 * 1000;
								const isLatestUser = m.role === 'user' && m.id === latestUserMessageId;
								const fb = feedback[m.id];

								return (
									<React.Fragment key={m.id}>
										{showSep && (
											<div className="ai-chat-panel__date-sep">
												{formatDateSeparator(m.createdAt)}
											</div>
										)}
										<div
											className={`ai-chat-panel__message ai-chat-panel__message--${m.role}`}
										>
											<div className="ai-chat-panel__bubble">
												{renderMessageContent(m.content)}
											</div>
											{m.unsaved && (
												// The answer above is real and was
												// delivered; the backend just could not
												// write it to the transcript, so it will
												// not be here when this conversation is
												// reopened. Said plainly rather than left
												// for the user to discover tomorrow.
												<div
													className="ai-chat-panel__msg-note"
													role="note"
												>
													Not saved to this conversation — it
													won&apos;t appear when you reopen it.
												</div>
											)}

											<div className="ai-chat-panel__msg-actions">
												{m.role === 'assistant' && m.id !== 'welcome-hfcl-chatbot' && (
													<>
														<button
															type="button"
															className={`ai-chat-panel__msg-action${fb === 'up' ? ' is-active' : ''}`}
															aria-label="Like response"
															onClick={() => handleFeedback(m.id, 'up')}
														>
															<IoIcon src={IO_ICONS.like} size={16} />
														</button>
														<button
															type="button"
															className={`ai-chat-panel__msg-action${fb === 'down' ? ' is-active' : ''}`}
															aria-label="Dislike response"
															onClick={() => handleFeedback(m.id, 'down')}
														>
															<IoIcon src={IO_ICONS.dislike} size={16} />
														</button>
													</>
												)}
												<button
													type="button"
													className="ai-chat-panel__msg-action"
													aria-label={m.role === 'user' ? 'Copy prompt' : 'Copy response'}
													title={m.role === 'user' ? 'Copy prompt' : 'Copy response'}
													onClick={() => handleCopy(m.content, m.role)}
												>
													<IoIcon src={IO_ICONS.copy} size={16} />
												</button>
												{isLatestUser && (
													<button
														type="button"
														className="ai-chat-panel__msg-action"
														aria-label="Edit prompt"
														title="Edit prompt"
														onClick={() => handleEditLatestUser(m)}
													>
														<IoIcon src={IO_ICONS.edit} size={16} />
													</button>
												)}
											</div>

											{m.role === 'assistant' && m.navigation && (() => {
												const isHandled = handledIds.has(m.id);
												return (
													<div className="ai-chat-panel__offer-actions">
														<button
															type="button"
															className={`ai-chat-panel__offer-btn ai-chat-panel__offer-btn--primary${isHandled ? ' ai-chat-panel__offer-btn--disabled' : ''}`}
															onClick={() => handleOfferYes(m.navigation, m.id)}
															disabled={isHandled}
														>
															Yes, proceed
														</button>
														<button
															type="button"
															className={`ai-chat-panel__offer-btn${isHandled ? ' ai-chat-panel__offer-btn--disabled' : ''}`}
															onClick={() => handleOfferNo(m.id)}
															disabled={isHandled}
														>
															No, thanks
														</button>
													</div>
												);
											})()}
											{m.role === 'assistant' && Array.isArray(m.navigations) && m.navigations.length > 0 && (
												<OfferChips
													items={m.navigations}
													disabled={handledIds.has(m.id)}
													onSelect={(opt) => handleOfferYes(opt, m.id)}
													keyFor={(opt, i) => `${opt.path}-${i}`}
													labelFor={(opt) => opt.label}
												/>
											)}
											{m.role === 'assistant' && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
												<OfferChips
													items={m.suggestions}
													disabled={handledIds.has(m.id)}
													onSelect={(suggestion) => handleSuggestionClick(suggestion, m.id)}
													keyFor={(suggestion, i) => `s-${i}`}
													labelFor={(suggestion) => suggestion.label}
												/>
											)}
										</div>
									</React.Fragment>
								);
							})}

							{loading && (
								<div className="ai-chat-panel__message ai-chat-panel__message--assistant">
									<div
										className="ai-chat-panel__bubble ai-chat-panel__bubble--thinking"
										role="status"
										aria-live="polite"
										aria-label={statusLine || 'Thinking'}
									>
										<span className="ai-chat-panel__typing-dot" />
										<span className="ai-chat-panel__typing-dot" />
										<span className="ai-chat-panel__typing-dot" />
									</div>
									{statusLine && (
										<div className="ai-chat-panel__status-line">
											{statusLine}
										</div>
									)}
								</div>
							)}
							</div>
							{showJumpToLatest && (
								<button
									type="button"
									className="ai-chat-panel__jump-latest"
									onClick={jumpToLatest}
									aria-label="Jump to latest"
									title="Jump to latest"
								>
									<KeyboardArrowDownIcon fontSize="inherit" />
								</button>
							)}
						</div>

						<div className="ai-chat-panel__footer">
							{/* Starter chips only on a fresh chat. Hide after the first question. */}
							{!messages.some((m) => m.role === 'user') && (
							<div className="ai-chat-panel__chips">
								{STARTER_CHIPS.map((chip) => (
									<button
										key={chip}
										type="button"
										className={`ai-chat-panel__chip${selectedChip === chip ? ' ai-chat-panel__chip--selected' : ''}`}
										title={chip}
										disabled={loading}
										onClick={() => handleStarterChip(chip)}
									>
										<span className="ai-chat-panel__chip-label">{chip}</span>
									</button>
								))}
							</div>
							)}

							{voicePhase === 'recording' ? (
								<div
									className="ai-chat-panel__composer ai-chat-panel__composer--recording"
									role="status"
									aria-live="polite"
									aria-label="Listening"
								>
									<button
										type="button"
										className="ai-chat-panel__rec-cancel"
										onClick={handleCancelRecording}
										aria-label="Cancel dictation"
									>
										<IoIcon src={IO_ICONS.close} size={16} />
									</button>
									<WaveformBars />
									<Tooltip
										content="Stop Dictating"
										type="basic"
										direction="top"
										style={{ width: '110px' }}
									>
										<button
											type="button"
											className="ai-chat-panel__send-btn ai-chat-panel__send-btn--active ai-chat-panel__send-btn--glyph"
											onClick={handleStopDictating}
											aria-label="Stop dictating"
										>
											<StopRoundedIcon />
										</button>
									</Tooltip>
								</div>
							) : voicePhase === 'transcribing' ? (
								<div
									className="ai-chat-panel__composer ai-chat-panel__composer--transcribing"
									role="status"
									aria-live="polite"
									aria-label="Transcribing"
								>
									<button
										type="button"
										className="ai-chat-panel__rec-cancel"
										onClick={handleCancelRecording}
										aria-label="Cancel transcription"
									>
										<IoIcon src={IO_ICONS.close} size={16} />
									</button>
									<div className="ai-chat-panel__transcribing">
										<CircularProgress
											size={18}
											thickness={4}
											sx={{ color: '#500d76' }}
										/>
										<span>Transcribing...</span>
									</div>
									<button
										type="button"
										className="ai-chat-panel__send-btn"
										disabled
										aria-label="Send message"
									>
										<IoIcon src={IO_ICONS.sendIdle} size={32} className="ai-chat-panel__io-icon--send" />
									</button>
								</div>
							) : (
								<form
									className={`ai-chat-panel__composer${inputFocused ? ' ai-chat-panel__composer--focused' : ''}${hasContent ? ' ai-chat-panel__composer--has-content' : ''}`}
									onSubmit={handleSubmit}
								>
									<textarea
										ref={inputRef}
										className="ai-chat-panel__input"
										placeholder={inputPlaceholder}
										value={message}
										rows={1}
										onChange={(e) => setMessage(e.target.value)}
										onKeyDown={handleKeyDown}
										onFocus={() => setInputFocused(true)}
										onBlur={() => setInputFocused(false)}
										aria-label="Chat prompt"
									/>
									<div className="ai-chat-panel__composer-toolbar">
										<Tooltip
											content="Use microphone"
											type="basic"
											direction="top"
											style={{ width: '120px' }}
										>
											<button
												type="button"
												className="ai-chat-panel__mic-btn"
												onClick={handleMicClick}
												disabled={!speech.supported}
												aria-label={
													!speech.supported
														? 'Voice input not supported in this browser'
														: `Start voice input (${detectedSpeechLang})`
												}
											>
												<IoIcon src={IO_ICONS.mic} />
											</button>
										</Tooltip>
										<button
											type="submit"
											className={`ai-chat-panel__send-btn${hasContent ? ' ai-chat-panel__send-btn--active' : ''}`}
											disabled={!hasContent || loading}
											aria-label="Send message"
										>
											<IoIcon
												src={hasContent ? IO_ICONS.sendActive : IO_ICONS.sendIdle}
												size={32}
												className="ai-chat-panel__io-icon--send"
											/>
										</button>
									</div>
								</form>
							)}

							<p className="ai-chat-panel__disclaimer">
								io Genie can make mistakes. Verify important info.
							</p>
						</div>
					</>
				)}
			</div>

			{deleteConfirmThread && (
				<div className="ai-chat-panel__modal-backdrop" role="presentation">
					<div
						className="ai-chat-panel__modal"
						role="alertdialog"
						aria-labelledby="io-genie-delete-title"
						aria-describedby="io-genie-delete-desc"
					>
						<button
							type="button"
							className="ai-chat-panel__modal-close"
							aria-label="Close"
							onClick={() => setDeleteConfirmThread(null)}
						>
							<IoIcon src={IO_ICONS.close} size={16} />
						</button>
						<div className="ai-chat-panel__modal-head">
							<WarningAmberRoundedIcon className="ai-chat-panel__modal-warn" />
							<h2 id="io-genie-delete-title">
								Are you sure you want to delete thread?
							</h2>
						</div>
						<p id="io-genie-delete-desc" className="ai-chat-panel__modal-body">
							You will no longer see this conversation in your thread list. This will
							also delete related activity like prompts, responses and feedback from
							your io Genie activity.
						</p>
						<div className="ai-chat-panel__modal-actions">
							<button
								type="button"
								className="ai-chat-panel__modal-cancel"
								onClick={() => setDeleteConfirmThread(null)}
							>
								Cancel
							</button>
							<button
								type="button"
								className="ai-chat-panel__modal-delete"
								onClick={confirmDeleteThread}
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

AiChatPanel.propTypes = {
	onClose: PropTypes.func.isRequired,
	isMinimized: PropTypes.bool.isRequired,
	onMinimize: PropTypes.func.isRequired,
	onRestore: PropTypes.func.isRequired,
	hidden: PropTypes.bool,
	onAction: PropTypes.func,
	onNewReplyWhileHidden: PropTypes.func,
};

export default AiChatPanel;
